import React, { useCallback, useContext, useMemo, useState } from 'react'

export interface SplashHandle {
  id: number
  source: string
}

interface AppSplashContextValue {
  show: (source: string) => SplashHandle
  hide: (handle: SplashHandle) => void
  replace: (source: string) => SplashHandle
  isVisible: boolean
  activeSource?: string
}

const DEFAULT_CONTEXT: AppSplashContextValue = {
  show: source => ({ id: -1, source }),
  hide: () => undefined,
  replace: source => ({ id: -1, source }),
  isVisible: false,
  activeSource: undefined
}

const AppSplashContext = React.createContext<AppSplashContextValue>(DEFAULT_CONTEXT)

let nextHandleId = 1

export const AppSplashProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeHandle, setActiveHandle] = useState<SplashHandle | null>(null)

  const show = useCallback((source: string): SplashHandle => {
    const handle = { id: nextHandleId++, source }
    setActiveHandle(handle)
    return handle
  }, [])

  const hide = useCallback((handle: SplashHandle): void => {
    setActiveHandle(current => {
      if (!current) return current
      return current.id === handle.id ? null : current
    })
  }, [])

  const replace = useCallback(
    (source: string): SplashHandle => {
      return show(source)
    },
    [show]
  )

  const contextValue = useMemo<AppSplashContextValue>(
    () => ({
      show,
      hide,
      replace,
      isVisible: Boolean(activeHandle),
      activeSource: activeHandle?.source
    }),
    [show, hide, replace, activeHandle]
  )

  return <AppSplashContext.Provider value={contextValue}>{children}</AppSplashContext.Provider>
}

export const useAppSplash = (): AppSplashContextValue => {
  return useContext(AppSplashContext)
}
